import { useState, useEffect } from 'react';
import { usePage } from '@inertiajs/react';

/**
 * Flash message props from the backend.
 * Uses index signature to satisfy PageProps constraint.
 */
interface FlashProps {
    flash?: {
        success?: string;
        error?: string;
    };
    [key: string]: unknown;
}

/**
 * FlashMessage component - displays success/error messages from backend redirects.
 * Auto-dismisses after a configurable timeout.
 */
export function FlashMessage({ autoDismiss = 5000 }: { autoDismiss?: number }) {
    const { flash } = usePage<FlashProps>().props;
    const [visible, setVisible] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Update message when flash changes
    useEffect(() => {
        if (flash?.success) {
            setMessage({ type: 'success', text: flash.success });
            setVisible(true);
        } else if (flash?.error) {
            setMessage({ type: 'error', text: flash.error });
            setVisible(true);
        }
    }, [flash?.success, flash?.error]);

    // Auto-dismiss timer
    useEffect(() => {
        if (!visible || autoDismiss <= 0) return;

        const timer = setTimeout(() => {
            setVisible(false);
        }, autoDismiss);

        return () => clearTimeout(timer);
    }, [visible, autoDismiss]);

    if (!visible || !message) return null;

    const bgClass = message.type === 'success'
        ? 'bg-green-900/90 border-green-500'
        : 'bg-red-900/90 border-red-500';

    const textClass = message.type === 'success'
        ? 'text-green-200'
        : 'text-red-200';

    const icon = message.type === 'success' ? '✓' : '⚠️';

    return (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
            <div className={`${bgClass} border rounded-lg p-4 shadow-xl max-w-sm`}>
                <div className="flex items-start gap-3">
                    <span className={textClass}>{icon}</span>
                    <p className={`${textClass} text-sm flex-1`}>{message.text}</p>
                    <button
                        onClick={() => setVisible(false)}
                        className={`${textClass} hover:opacity-70 transition-opacity`}
                    >
                        ✕
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Inline flash message for embedding in page content.
 */
export function InlineFlashMessage() {
    const { flash } = usePage<FlashProps>().props;

    if (!flash?.success && !flash?.error) return null;

    const isSuccess = !!flash.success;
    const message = flash.success || flash.error;

    const bgClass = isSuccess
        ? 'bg-green-900/50 border-green-500'
        : 'bg-red-900/50 border-red-500';

    const textClass = isSuccess
        ? 'text-green-200'
        : 'text-red-200';

    const icon = isSuccess ? '✓' : '⚠️';

    return (
        <div className={`${bgClass} border-l-4 rounded-lg p-4 mb-6`}>
            <div className="flex items-center gap-2">
                <span>{icon}</span>
                <span className={textClass}>{message}</span>
            </div>
        </div>
    );
}

export default FlashMessage;

